import {Injectable} from '@angular/core';
import {MatSnackBar, MatSnackBarConfig, MatSnackBarRef} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {StatusComponent} from '../../components/status/status.component';
import {UtilService} from '../util/util.service';
import {MatDialog} from '@angular/material/dialog';
import {StorageService} from '../storage/storage.service';

@Injectable()
export class SnackService {
  public static SNACK_FIXED_MESSAGE = 'fixedStatusMessage';

  constructor(private snackBar: MatSnackBar,
              private translateService: TranslateService,
              public utilService: UtilService,
              private dialog: MatDialog,
              private storageService: StorageService) {}

  private _timeouts: any[];
  private _snackRef: MatSnackBarRef<any>;
  private _snackBarOpened: boolean;
  private _snackBarFixed: boolean;
  private _snackBarDouble: boolean;
  private _snackBarHeight: number;
  private _hidden: boolean;
  private _storedAction: any;

  public get snackHeight(): number {
    return this._snackBarHeight;
  }
  public set snackHeight(value) {
    this._snackBarHeight = value;
  }

  public get hidden(): boolean {
    return this._hidden;
  }
  public set hidden(value) {
    this._hidden = value;
  }

  public get isOpened(): boolean {
    return this._snackBarOpened;
  }

  public get isNotFixedOpen(): boolean {
    return this._snackBarOpened && !this._snackBarFixed;
  }

  public showInfo(message: string | string[], fixed?: boolean, action?: Function, actionName?: string): void {
    this.showWithDefaults(message, 'info', fixed, action, actionName);
  }

  public showSuccess(message: string | string[], fixed?: boolean, action?: Function, actionName?: string): void {
    this.showWithDefaults(message, 'success', fixed, action, actionName);
  }

  public showError(message: string | string[], fixed?: boolean, action?: Function, actionName?: string): void {
    this.showWithDefaults(message, 'warning', fixed, action, actionName);
  }

  public showBackendError(data: any, text = ''): void {
    let message = text;
    if (data?.error?.message) {
      message = data.error.message;
      message = 'general.' + message;
      if (data.error.code === 'token_refresh_required') {
        this.utilService.tokenRefresh.emit();
        return;
      }
      if (['token_not_valid', 'token_invalid', 'api_token_not_valid'].includes((data.error?.code || data.error?.message)?.toLowerCase())) {
        this.utilService.tokenExpired.emit();
      }
    }
    if (message) {
      this.showError(this.translateService.instant(message));
    } else {
      console.error(data);
    }
  }

  private showWithDefaults(message: string | string[], cssClass?: string, fixed?: boolean, action?: Function, actionName?: string): void {
    const config: MatSnackBarConfig = {
      panelClass: cssClass,
      politeness: 'assertive',
      data: {
        message: message,
        action: action,
        actionName: actionName
      }
    };
    if (!fixed) {
      config.duration = 10000;
    } else {
      this.utilService.fixedSnack.emit(true);
    }
    this.openMessage(config, fixed);
  }

  private openMessage(config: MatSnackBarConfig, fixed?: boolean): void {
    const dialogs = this.dialog.openDialogs;
    let ifAnyDialog = this.dialog.openDialogs?.length && dialogs[this.dialog.openDialogs.length-1];
    this._timeouts = [];
    this._timeouts.push(setTimeout(() => { // Allow to close dialogs first, then check if there are any left and use the last one
      ifAnyDialog = this.dialog.openDialogs?.length && dialogs[this.dialog.openDialogs.length-1]; // Check once more on timeout
      this._timeouts.splice(0, 1);
      if (this.snackBar._openedSnackBarRef) {
        if (!this._snackBarFixed) {
          this._snackBarDouble = true;
        }
        this.snackBar.dismiss();
      }
      this._timeouts.push(setTimeout(() => {
        this._timeouts.splice(0, 1);
        this._snackRef = this.snackBar.openFromComponent(StatusComponent, config);
        if (this.hidden || ifAnyDialog) {
          this.hide();
        }
        this._snackRef.afterOpened().subscribe(() => {
          this._snackBarOpened = true;
          this.resize();
          window.addEventListener('resize', this.resize);
          if (fixed) {
            this.storageService.set(SnackService.SNACK_FIXED_MESSAGE, JSON.stringify(config));
            if (config.data?.action) {
              this._storedAction = config.data.action;
            }
            this._snackBarFixed = true;
            if (!ifAnyDialog) {
              this.utilService.fixedSnackHeight = this.snackHeight;
            }
          }
        });
        this._snackRef.afterDismissed().subscribe((dismiss) => {
          this.utilService.snackDismissed.next(new Date());
          this.utilService.fixedSnack.emit(false);
          this._snackBarOpened = false;
          this._snackBarFixed = false;
          window.removeEventListener('resize', this.resize);
          this.resize();
          this.utilService.fixedSnackHeight = 0;
          if (fixed && dismiss.dismissedByAction) {
            this.clearFixedSnackMessage();
          } else if (!fixed) {
            if (!this._snackBarDouble) {
              this.reOpenFixedMessage();
            }
            this._snackBarDouble = false;
          }
        });
        if (ifAnyDialog) {
          this.moveToDialog(fixed);
        }
      }, this.snackBar._openedSnackBarRef ? 250 : 0));
    }, ifAnyDialog ? 250 : 0));
  }

  public moveToDialog(fixed?: boolean): void {
    setTimeout(() => {
      let dialogRef = this.dialog.openDialogs[this.dialog.openDialogs.length-1];

      if (fixed) {
        this.dialog.openDialogs.forEach(dialog => {
          const container: any = dialog?._containerInstance;
          const el = container?._elementRef?.nativeElement;
          if (el && el.closest('.cdk-overlay-pane').classList.contains('sticky-notifications')) {
            dialogRef = dialog;
          }
        });
      }

      const dialogContainer: any = dialogRef?._containerInstance;
      if (dialogContainer) {
        const dialogElement = dialogContainer?._elementRef?.nativeElement;
        const snackContainer: any = this._snackRef?.containerInstance;
        const snackElement = snackContainer?._elementRef?.nativeElement;
        if (dialogElement && snackElement) {
          if (snackElement.parentElement?.tagName === 'DIV') {
            const snackOverlay = snackElement.closest('.cdk-global-overlay-wrapper');
            if (snackOverlay) {
              snackOverlay.style.display = 'none';
            }
          }
          dialogElement.querySelector('.mdc-dialog__surface').prepend(snackElement);
          this.show();
        }
        if (snackElement) {
          this._snackRef.afterDismissed().subscribe( () => {
            snackElement.remove();
          });
        }
      }
    }, 0);
  }

  private resize(): void {
    const snackContainer = document.getElementsByClassName('mat-snack-bar-container')[0];
    this.snackHeight = window.innerWidth <= 960 && snackContainer?.getBoundingClientRect().height || 0;
    if (snackContainer) {
      const snackWrapper: any = snackContainer.querySelector('.wrapper');
      const dialogContainer: any = snackContainer.closest('.mdc-dialog__container');
      if (dialogContainer && this.snackHeight > 0) {
        dialogContainer.style.paddingTop = this.snackHeight + (snackWrapper ? parseFloat(getComputedStyle(snackWrapper).paddingTop) : 0) + 'px';
      } else {
        const dialogQuery: any = document.querySelector('.mdc-dialog__container');
        if (dialogQuery) {
          dialogQuery.style.paddingTop = null;
        }
      }
    }
  }

  public clearAll(): void {
    this.snackBar.dismiss();
    if (this._timeouts?.length) {
      this._timeouts.forEach(timeout => clearTimeout(timeout));
      this._timeouts = [];
    }
  }

  public clearFixedSnackMessage(): void {
    this.storageService.remove(SnackService.SNACK_FIXED_MESSAGE);
    this._storedAction = null;
  }

  private reOpenFixedMessage(): void {
    const config: MatSnackBarConfig = JSON.parse(this.storageService.get(SnackService.SNACK_FIXED_MESSAGE)) as MatSnackBarConfig;
    if (config?.data) {
      if (this._storedAction) {
        config.data.action = this._storedAction;
      }
      this.openMessage(config, true);
    }
  }

  public hide(): void {
    const snackContainer: any = this._snackRef.containerInstance;
    const snackElement = snackContainer?._elementRef?.nativeElement;
    if (snackElement) {
      snackElement.style.display = 'none';
      this.resize();
    }
  }

  public show(): void {
    const snackContainer: any = this._snackRef.containerInstance;
    const snackElement = snackContainer?._elementRef?.nativeElement;
    if (snackElement) {
      snackElement.style.display = '';
      this.resize();
    }
    this.hidden = false;
  }
}
